/**
 * F&F Finance Posting Staging — finance team's worklist.
 *
 * Route: /hr/finance-posting
 *
 * When an F&F is PAID, fnf_calculation_service auto-creates one posting
 * line per non-zero bucket (basic, allowances, recoveries, net to bank,
 * etc.) — each with a default debit/credit ledger pair.
 *
 * Finance team:
 *   1. Reviews PENDING_REVIEW lines, marks APPROVED
 *   2. Posts to tally / books, then marks POSTED with voucher_id
 *   3. Or REJECTS / SKIPS with a remark
 *
 * Pure staging — actual JV posting still happens in their accounting tool;
 * this just gives them a clean, traceable worklist.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Card, Form, Input, Modal, Select, Space, Statistic, Table, Tag,
  Typography, message,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'

import {
  separationApi, POSTING_STATUS_COLOR, type FinancePostingLine,
} from '@/features/hr/separationApi'

const { Title, Text } = Typography

const POSTING_STATUSES = [
  'PENDING_REVIEW', 'APPROVED', 'POSTED', 'REJECTED', 'SKIPPED',
] as const

const HEAD_TYPE_COLOR: Record<string, string> = {
  EARNING: 'blue',
  DEDUCTION: 'orange',
  RECOVERY: 'red',
  NET: 'green',
}

export default function FinancePostingPage() {
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<string>('PENDING_REVIEW')
  const [actionRow, setActionRow] = useState<FinancePostingLine | null>(null)
  const [actionForm] = Form.useForm()

  const listQ = useQuery({
    queryKey: ['finance-posting-lines', statusFilter],
    queryFn: () => separationApi.listFinancePostingLines({
      posting_status: statusFilter === 'ALL' ? undefined : statusFilter,
    }),
  })

  const actionMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof separationApi.actionFinancePostingLine>[1] }) =>
      separationApi.actionFinancePostingLine(id, body),
    onSuccess: () => {
      message.success('Line updated')
      setActionRow(null)
      actionForm.resetFields()
      qc.invalidateQueries({ queryKey: ['finance-posting-lines'] })
    },
    onError: () => message.error('Failed to update line'),
  })

  const rows = listQ.data ?? []
  const totalAmount = rows.reduce((sum, r) => sum + (r.amount || 0), 0)
  const pendingCount = rows.filter((r) => r.posting_status === 'PENDING_REVIEW').length

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>F&F Finance Posting</Title>
      <Text type="secondary">
        Posting lines auto-generated when an F&F payment is recorded.
        Finance team reviews, posts to tally, and marks POSTED with voucher reference.
      </Text>

      <Space size="large" style={{ marginTop: 16, marginBottom: 16 }}>
        <Statistic title="Lines in view" value={rows.length} />
        <Statistic title="Pending review" value={pendingCount} valueStyle={{ color: '#fa8c16' }} />
        <Statistic
          title="Total amount"
          value={totalAmount}
          precision={2}
          prefix="₹"
        />
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text>Status:</Text>
          <Select
            style={{ width: 200 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'ALL', label: 'All' },
              ...POSTING_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') })),
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>Refresh</Button>
        </Space>
      </Card>

      <Table<FinancePostingLine>
        size="small"
        rowKey="id"
        loading={listQ.isLoading}
        dataSource={rows}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: 'Employee', dataIndex: 'employee_code', key: 'employee_code', width: 110 },
          {
            title: 'Field',
            dataIndex: 'posting_field',
            key: 'posting_field',
            width: 200,
            render: (v: string) => (
              <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text>
            ),
          },
          {
            title: 'Head',
            dataIndex: 'head_type',
            key: 'head_type',
            width: 100,
            render: (t: string) => <Tag color={HEAD_TYPE_COLOR[t]}>{t}</Tag>,
          },
          {
            title: 'Amount (₹)',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            width: 120,
            render: (v: number) => v.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
          },
          {
            title: 'Debit Ledger',
            dataIndex: 'debit_ledger',
            key: 'debit_ledger',
            width: 200,
            render: (v: string | null) => (
              <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text>
            ),
          },
          {
            title: 'Credit Ledger',
            dataIndex: 'credit_ledger',
            key: 'credit_ledger',
            width: 200,
            render: (v: string | null) => (
              <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'posting_status',
            key: 'posting_status',
            width: 130,
            render: (s: FinancePostingLine['posting_status']) => (
              <Tag color={POSTING_STATUS_COLOR[s]}>{s.replace('_', ' ')}</Tag>
            ),
          },
          {
            title: 'Voucher',
            dataIndex: 'posted_voucher_id',
            key: 'posted_voucher_id',
            width: 140,
            render: (v: string | null) => v
              ? <Text style={{ fontSize: 12 }}>{v.slice(0, 8)}…</Text>
              : '-',
          },
          {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 90,
            render: (_, r) => r.posting_status !== 'POSTED' && r.posting_status !== 'REJECTED' ? (
              <Button size="small" onClick={() => {
                setActionRow(r)
                actionForm.setFieldsValue({
                  new_status: r.posting_status === 'PENDING_REVIEW' ? 'APPROVED' : 'POSTED',
                })
              }}>
                Action
              </Button>
            ) : null,
          },
        ]}
      />

      <Modal
        title="Update Posting Line"
        open={!!actionRow}
        onCancel={() => setActionRow(null)}
        onOk={() => actionForm.submit()}
        confirmLoading={actionMut.isPending}
        destroyOnClose
      >
        {actionRow && (
          <Form
            form={actionForm}
            layout="vertical"
            onFinish={(v) => actionMut.mutate({
              id: actionRow.id,
              body: {
                new_status: v.new_status,
                voucher_id: v.voucher_id || undefined,
                remarks: v.remarks,
              },
            })}
          >
            <Text type="secondary">
              {actionRow.employee_code} · {actionRow.posting_field} · ₹{actionRow.amount.toLocaleString('en-IN')}
            </Text>
            <div style={{ height: 12 }} />
            <Form.Item name="new_status" label="New Status" rules={[{ required: true }]}>
              <Select
                options={POSTING_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
              />
            </Form.Item>
            <Form.Item
              name="voucher_id"
              label="Voucher ID (UUID, required for POSTED)"
              tooltip="The voucher you created in your accounting tool"
            >
              <Input placeholder="Optional — required when marking POSTED" />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  )
}
